<template>
  <div id="wrapper">
    
    <!-- Terms of Use Modal -->

    <div v-if="showTermsModal" class="modal is-active">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Terms of Use</p>
          <button class="delete" aria-label="close" @click="showTermsModal = false"></button>
        </header>
        <section class="modal-card-body">
          <div class="content">
            <h4>Move To Production</h4>
            <p>Please review the following terms and conditions before proceeding to move your account to production.</p>
            
            <!-- Privacy Policy and Terms of Use Links -->
            <p>
              By continuing, you agree to our 
              <a href="https://www.despaq.com/privacy-policy" target="_blank">Privacy Policy</a> 
              and 
              <a href="https://www.despaq.com/terms-and-condition" target="_blank">Terms of Use</a>.
            </p>

            <p>
              User: <strong>{{ userProfile.first_name }} {{ userProfile.last_name }}</strong> <br>
              Email: <strong>{{ userProfile.email }}</strong> <br>
              Date & Time: <strong>{{ currentDateTime }}</strong>
            </p>
          </div>
          <div class="field">
            <input type="checkbox" id="acceptTerms" v-model="acceptTerms">
            <label for="acceptTerms">  I accept to move the account to production.</label>
          </div>
        </section>
        <footer class="buttons modal-card-foot">
          <button class="button is-success" :disabled="!acceptTerms" @click="moveToProduction">Confirm</button>
          <button class="button" @click="showTermsModal = false">Cancel</button>
        </footer>
      </div>
    </div>
    <nav class="navbar is-dark is-fixed-top">
      <div class="navbar-brand">
        <router-link to="/" class="navbar-item has-text-white">
          <strong>DespaQ E-Invoicing Portal</strong>
        </router-link>
      </div>
      <div class="navbar-menu has-text-centered">
        <div class="navbar-start has-text-centered">
          <template v-if="$store.state.isAuthenticated">
            <span class="navbar-item has-text-white has-text-centered">
              Welcome back, {{ userProfile.first_name }} {{ userProfile.last_name }} !
            </span>
          </template>
        </div>
        <div v-if="isSandbox" class=" columns sandbox-banner is-vcentered">
          <p class="is-vcentered">You're in Sandbox Mode!</p>
          <button @click="showTermsModal = true" class="button is-primary is-small ml-4">Move to Production</button>
        </div>
        <div class="navbar-end">
          <template v-if="$store.state.isAuthenticated">
            <div class="navbar-item">
              <img v-if="userProfile.image" :src="userProfile.image" class="user-avatar" alt="User Avatar">
            </div>
            <router-link to="/dashboard/my-account" class="navbar-item has-text-white pr-5">
              My Account
            </router-link>
          </template>
          <template v-else>
            <div class="navbar-item">
              <p>itsupport@DespaQ.com</p>
            </div>
          </template>
        </div>
      </div>
    </nav>

    <aside v-if="$store.state.isAuthenticated && !['/log-in', '/'].includes(this.$route.path)" class="menu" style="margin-top: 52px;">
      <div class="menu-header">
        <img :src="logoUrl" alt="DespaQ Logo" class="menu-logo">
      </div>
      <ul class="menu-list">
        <li>
          <router-link to="/dashboard" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="tachometer-alt" class="icon is-small"></font-awesome-icon> Dashboard
          </router-link>
        </li>
        <li v-if="canViewInvoice || canViewAll">
          <router-link to="/dashboard/invoices" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="file-invoice" class="icon is-small"></font-awesome-icon> E-Invoices
          </router-link>
        </li>
        <li v-if="canCreateInvoice">
          <router-link to="/dashboard/create-document" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="plus" class="icon is-small"></font-awesome-icon> Create
          </router-link>
        </li>
        <li v-if="canApproveInvoice">
          <router-link to="/dashboard/invoices/approval" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="check" class="icon is-small"></font-awesome-icon> Approve
          </router-link>
        </li>
        <li v-if="canCreateInvoice || canViewAll">
          <router-link to="/dashboard/customers" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="users" class="icon is-small"></font-awesome-icon> Customers
          </router-link>
        </li>
        <li v-if="canCreateInvoice || canViewAll">
          <router-link to="/dashboard/itemstable" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="boxes" class="icon is-small"></font-awesome-icon> Saved Items
          </router-link>
        </li>
        <li v-if="canExportCsv || canViewAll">
          <router-link to="/dashboard/report" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="file" class="icon is-small"></font-awesome-icon> Reporting
          </router-link>
        </li>
        <li>
          <router-link to="/dashboard/my-account" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="users" class="icon is-small"></font-awesome-icon> My Team
          </router-link>
        </li>
        <li v-if="isSuperAdmin">
          <router-link to="/admin" class="menu-item" active-class="is-active">
            <font-awesome-icon icon="users" class="icon is-small"></font-awesome-icon> Admin Setting
          </router-link>
        </li>
      </ul>
      <div class="menu-footer">
        <ul class="menu-list">
          <a href="https://www.hasil.gov.my/en/e-invoice/" class="menu-item">
              <font-awesome-icon icon="info" class="icon is-small"></font-awesome-icon> LHDN Info
          </a>
          <a href="mailto:support@despaq.com" class="menu-item">
            <font-awesome-icon icon="headset" class="icon is-small"></font-awesome-icon> Get Support
          </a>
        </ul>
        <hr class="footer-separator">
        <div class="footer-content">
          <a @click.prevent="logout" class="footer-logout">
            <font-awesome-icon icon="sign-out-alt"></font-awesome-icon> Logout
          </a>
        </div>
      </div>
    </aside>

    <main :class="{'main-content': true, 'main-content-centered': !$store.state.isAuthenticated || ['/log-in', '/'].includes(this.$route.path)}">
      <section class="section">
        <router-view />
      </section>
      <footer class="footer has-background-light">
        <p class="has-text-centered">www.despaq.com | Copyright (c) 2024</p>
      </footer>
    </main>

    <div v-if="showModal && !['/log-in', '/'].includes(this.$route.path)" class="modal is-active">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">User Access Profile Missing</p>
          <button class="delete" aria-label="close" @click="showModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p>The User Access Profile for the User ({{ userProfile.username }}) has not been created, please contact your IT team to enable your profile.</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-danger" @click="logout">Log Out</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTachometerAlt, faFileInvoice, faPlus, faUsers, faBoxes, faSignOutAlt, faCheck, faUser, faHeadset, faInfo, faFile } from '@fortawesome/free-solid-svg-icons';
import store from './store';

library.add(faTachometerAlt, faFileInvoice, faPlus, faUsers, faBoxes, faSignOutAlt, faCheck, faUser, faHeadset, faInfo, faFile);

export default {
  name: 'App',
  store,
  components: {
    FontAwesomeIcon,
  },
  data() {
    return {
      logoUrl: 'https://parkson.s3.ap-southeast-1.amazonaws.com/Frontend/Logo_DespaQ.png',
      isUserProfileLoaded: false,
      showModal: false,
      idleTimeout: null, // Variable to store the timeout
      idleTimeLimit: 15 * 60 * 1000, // 15 minutes in milliseconds
      currentDateTime: new Date().toLocaleString(),
      showTermsModal: false, // To show/hide the terms modal
      acceptTerms: false, // To track whether the user accepts the terms
    };
  },
  async mounted() {
    await this.getUserProfile();

    // Add event listeners for user activity
    window.addEventListener('mousemove', this.resetIdleTimer);
    window.addEventListener('keydown', this.resetIdleTimer);
    window.addEventListener('mousedown', this.resetIdleTimer);
    window.addEventListener('touchstart', this.resetIdleTimer);

    // Start the idle timeout
    this.startIdleTimeout();

    // Add the beforeunload event listener
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  
  beforeUnmount() {
    // Remove event listeners for user activity
    window.removeEventListener('mousemove', this.resetIdleTimer);
    window.removeEventListener('keydown', this.resetIdleTimer);
    window.removeEventListener('mousedown', this.resetIdleTimer);
    window.removeEventListener('touchstart', this.resetIdleTimer);

    // Remove the beforeunload event listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },

  methods: {
    async moveToProduction() {
      try {
        
        // Make API request to update sandbox status to false
        const response = await axios.patch(`/api/v1/authentication/tenant/production/`, {}, {
          headers: {
            'Authorization': `Token ${this.$store.state.token}`
          }
        });

        // Handle successful response
        if (response.status === 200) {
          alert('Your account has been successfully moved to production!');
          this.showTermsModal = false;
          this.acceptTerms = false;
          this.logout();

          // Optionally, update your frontend state or Vuex store here if needed
        }
      } catch (error) {
        // Handle errors
        if (error.response && error.response.status === 403) {
          alert('Only superusers are allowed to perform this action.');
        } else if (error.response && error.response.status === 404) {
          alert('Tenant not found.');
        } else {
          console.error('An error occurred:', error);
          alert('An error occurred while trying to move your account to production.');
        }
      }
    },
    startIdleTimeout() {
      this.idleTimeout = setTimeout(this.logout, this.idleTimeLimit);
    },
    resetIdleTimer() {
      // Clear the existing timeout and start a new one
      clearTimeout(this.idleTimeout);
      this.startIdleTimeout();
    },
    async getUserProfile() {
      try {
        await this.$store.dispatch('fetchUserProfile'); // Fetch user profile from the store
        this.isUserProfileLoaded = true;
        // Permissions are now part of the user profile, no need to fetch separately
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push('/log-in');
        } else {
          console.log(JSON.stringify(error));
        }
      }
    },
    handleBeforeUnload(event) {
      // Do nothing on refresh, just prevent logging out during refresh.
      // You can display a warning message if needed.
      event.preventDefault();
      event.returnValue = '';
    },
    async logout() {
      try {
        this.showModal = false;
        await axios.post("/api/v1/token/logout/");

        // Clear local storage
        localStorage.clear();

        // Clear axios authorization header
        axios.defaults.headers.common["Authorization"] = "";

        // Commit Vuex mutation to remove token
        this.$store.commit('removeToken');

        // Redirect to home page
        this.$router.push('/log-in');
      } catch (error) {
        if (error.response) {
          console.log(JSON.stringify(error.response.data));
        } else if (error.message) {
          console.log(JSON.stringify(error.message));
        } else {
          console.log(JSON.stringify(error));
        }
      }
    },
    validateToken() {
      const token = this.$store.state.token;

      if (!token) {
        this.$router.push('/log-in');
      } else {
        axios.defaults.headers.common['Authorization'] = "Token " + token;

        // Optionally, you can make a test request to verify the token
        axios.get('/api/v1/validate-token/')
          .then(() => {
            // Token is valid, do nothing
          })
          .catch(() => {
            // Token is invalid, redirect to login
            this.$router.push('/log-in');
          });
      }
    }
  },
  computed: {
    userProfile() {
      return this.$store.state.user;
    },
    isSandbox() {
      return this.userProfile && this.userProfile.is_sandbox;
    },
    // Map getters to computed properties
    canCreateInvoice() {
      return this.$store.getters.canCreateInvoice;
    },
    canEditInvoice() {
      return this.$store.getters.canEditInvoice;
    },
    canViewInvoice() {
      return this.$store.getters.canViewInvoice;
    },
    canExportCsv() {
      return this.$store.getters.canExportCsv;
    },
    canApproveInvoice() {
      return this.$store.getters.canApproveInvoice;
    },
    canViewAll() {
      return this.$store.getters.canViewAll;
    },
    isSuperAdmin() {
      return this.$store.getters.isSuperAdmin;
    },
    hasRequiredFields() {
      return this.$store.state.user.sender_entities.length > 0 && this.$store.state.user.store_code.length > 0;
    },
    permissionsLoaded() {
      return this.isUserProfileLoaded && this.userProfile.permissions && Object.keys(this.userProfile.permissions).length > 0;
    }
  },
  created() {
    this.validateToken(); // Validate token after the component is created
  },
}
</script>


<style lang="scss">
@import '../node_modules/bulma';

#wrapper {
  display: flex;
  min-height: 100vh;

  nav.navbar {
    z-index: 1000;
    width: 100%;
    background-color: #0a1821;
  }

  nav.navbar .navbar-item:hover {
    background-color: #0a3954;
  }

  aside.menu {
    background-color: #082B3F;
    color: #fff;
    width: 200px;
    height: calc(100vh - 52px);
    position: fixed;
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    .menu-header {
      padding: 1rem;
      text-align: center;
    }

    .menu-logo {
      max-width: 150px;
    }

    .menu-list {
      padding-top: 0;
    }

    .menu-item {
      padding: 0.5rem 1.5rem;
      color: #fff;
      background-color: transparent;
      border-radius: 0;
      display: flex;
      align-items: center;
      gap: 10px;

      &:hover {
        background-color: #0a3954;
        color: #fff;
      }
    }

    .is-active {
      background-color: #0a3954;
      color: #fff;
      border-radius: 0;
    }

    .icon {
      margin-right: 0.5rem;
      font-size: 0.8rem; /* Smaller icon size */
    }

    ul {
      padding-left: 0;
      background-color: transparent;
    }

    .menu-footer {
      margin-top: auto;
      padding: 1rem;
      text-align: center;

      .footer-separator {
        border-color: #fff;
      }

      .footer-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;
      }

      .footer-logout {
        color: #fff;

        &:hover {
          color: #ff0000;
        }
      }
    }
  }

  .main-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin-left: 200px;

    .section {
      flex-grow: 1;
    }

    .footer {
      margin-top: auto;
      width: 100%;
      text-align: center;
      background-color: #f5f5f5;
      padding: 1rem;
    }

    .welcome-message {
      text-align: center;
      font-size: 1.5rem;
      margin-top: 1rem;
    }

    .user-avatar {
      width: 32px; /* Adjust width as per your design */
      height: 32px; /* Adjust height as per your design */
      border-radius: 50%; /* Makes the image round */
    }   
  }

  .main-content-centered {
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
    display: flex;
    justify-content: center;
  }

  .modal-card {
    max-width: 500px;
  }

  .sandbox-banner {
    color: #ffcc00;
    font-weight: bold;
    margin-top: 10px;
  }

  .modal {
    z-index: 1050 !important; /* Ensure it's higher than other elements */
  }
}

</style>
